import React, { useEffect, useState } from 'react';
import { Landmark, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';

const leer = {
  label: '',
  auth_method: 'push_tan',
  bank_code: '',
  endpoint_url: '',
  bank_user: '',
  customer_id: '',
  account_iban: '',
  account_bic: '',
};

function meldung(error, fallback) {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
}

function alsListe(daten) {
  if (Array.isArray(daten)) return daten;
  if (daten && daten.configured) return [daten];
  return [];
}

function felderAus(zeile) {
  return {
    label: zeile?.label || '',
    auth_method: zeile?.auth_method || 'tan',
    bank_code: zeile?.bank_code || '',
    endpoint_url: zeile?.endpoint_url || '',
    bank_user: zeile?.bank_user || '',
    customer_id: zeile?.customer_id || '',
    account_iban: zeile?.account_iban || '',
    account_bic: zeile?.account_bic || '',
  };
}

/**
 * PIN, danach TAN oder Bestätigung in der Banking-App, bei abweichendem
 * Empfängernamen eine Rückfrage. Die PIN bleibt nicht an der Bankverbindung.
 */
export default function FintsSubmitDialog({
  open, onOpenChange, targetType, targetId, lineIds, onSubmitted,
}) {
  const [liste, setListe] = useState([]);
  const [gewaehlt, setGewaehlt] = useState('');
  const [felder, setFelder] = useState(leer);
  const [aendern, setAendern] = useState(false);
  const [pin, setPin] = useState('');
  const [tan, setTan] = useState('');
  const [schritt, setSchritt] = useState('pin');
  const [sessionId, setSessionId] = useState('');
  const [hinweis, setHinweis] = useState('');
  const [entkoppelt, setEntkoppelt] = useState(false);
  const [verfahren, setVerfahren] = useState([]);
  const [busy, setBusy] = useState(false);

  const aktuelle = liste.find((z) => z.id === gewaehlt) || (liste.length === 1 ? liste[0] : null);

  useEffect(() => {
    if (!open) return undefined;
    setSchritt('pin');
    setPin('');
    setTan('');
    setSessionId('');
    setHinweis('');
    setEntkoppelt(false);
    setVerfahren([]);
    let aktiv = true;
    api.get('/sepa/fints/connections')
      .then((r) => {
        if (!aktiv) return;
        const zeilen = alsListe(r.data);
        setListe(zeilen);
        const erste = zeilen[0];
        setGewaehlt(erste?.id || '');
        setFelder(erste ? felderAus(erste) : leer);
        setAendern(zeilen.length === 0);
      })
      .catch(() => {
        if (!aktiv) return;
        setListe([]);
        setAendern(true);
      });
    return () => { aktiv = false; };
  }, [open, targetId]);

  const waehleBank = (id) => {
    setGewaehlt(id);
    const zeile = liste.find((z) => z.id === id);
    if (zeile) setFelder(felderAus(zeile));
  };

  const schliessen = (weiter) => {
    setPin('');
    setTan('');
    onOpenChange(weiter);
  };

  const uebernimm = (antwort) => {
    if (antwort.status === 'submitted') {
      toast.success(antwort.message || 'Bei der Bank eingereicht.');
      schliessen(false);
      if (onSubmitted) onSubmitted();
      return;
    }
    if (antwort.status === 'tan_wahl') {
      setVerfahren(antwort.mechanisms || []);
      setSchritt('tan_wahl');
      return;
    }
    setSessionId(antwort.session_id || '');
    setHinweis(antwort.challenge || '');
    setEntkoppelt(!!antwort.decoupled);
    setTan('');
    setSchritt(antwort.status === 'vop' ? 'vop' : 'tan');
  };

  const payload = () => ({
    label: felder.label,
    auth_method: felder.auth_method,
    bank_code: felder.bank_code,
    endpoint_url: felder.endpoint_url,
    bank_user: felder.bank_user,
    customer_id: felder.customer_id,
    account_iban: felder.account_iban,
    account_bic: felder.account_bic,
  });

  const speichereVerbindung = async () => {
    const koerper = payload();
    if (aktuelle?.id && aendern) {
      const r = await api.put(`/sepa/fints/connections/${aktuelle.id}`, koerper);
      return r.data;
    }
    if (!aktuelle) {
      const r = await api.post('/sepa/fints/connections', koerper);
      return r.data;
    }
    return aktuelle;
  };

  const senden = async (extra = {}) => {
    if (!pin.trim()) {
      toast.error('Die PIN fehlt.');
      return;
    }
    setBusy(true);
    try {
      let bank = aktuelle;
      if (aendern || !bank) {
        bank = await speichereVerbindung();
        if (!bank) return;
        setListe((alt) => {
          const ohne = alt.filter((z) => z.id !== bank.id);
          return [...ohne, bank];
        });
        setGewaehlt(bank.id || '');
        setAendern(false);
      }
      const body = {
        target_type: targetType,
        target_id: targetId,
        pin: pin.trim(),
      };
      if (bank?.id) body.connection_id = bank.id;
      if (lineIds && lineIds.length) body.line_ids = lineIds;
      if (extra.tan_security_function) {
        await api.put(`/sepa/fints/connections/${bank.id}`, {
          ...felderAus(bank),
          tan_security_function: extra.tan_security_function,
        });
      }
      const r = await api.post('/sepa/fints/submit', body);
      uebernimm(r.data);
    } catch (error) {
      toast.error(meldung(error, 'Die Bank hat den Auftrag nicht angenommen.'));
    } finally {
      setBusy(false);
    }
  };

  const fortsetzen = async (tanWert) => {
    setBusy(true);
    try {
      const r = await api.post('/sepa/fints/continue', {
        session_id: sessionId,
        tan: tanWert,
      });
      uebernimm(r.data);
    } catch (error) {
      toast.error(meldung(error, 'Die Bank hat den Auftrag nicht angenommen.'));
    } finally {
      setBusy(false);
    }
  };

  const nameDerBank = (zeile) => zeile.label || `${zeile.bank_code} · ${zeile.bank_user}`;

  return (
    <Dialog open={open} onOpenChange={schliessen}>
      <DialogContent className="max-w-md" data-testid="fints-dialog">
        <DialogHeader>
          <DialogTitle>An die Bank senden</DialogTitle>
          <DialogDescription>
            Der Auftrag geht über HBCI/FinTS direkt an die Bank. Die XML-Datei bleibt zusätzlich gespeichert.
          </DialogDescription>
        </DialogHeader>

        {schritt === 'pin' && (
          <div className="space-y-3">
            {liste.length > 1 && !aendern && (
              <div>
                <Label htmlFor="fints-bank">Bank</Label>
                <select
                  id="fints-bank"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={gewaehlt}
                  onChange={(e) => waehleBank(e.target.value)}
                  data-testid="fints-bank"
                >
                  {liste.map((z) => (
                    <option key={z.id} value={z.id}>{nameDerBank(z)}</option>
                  ))}
                </select>
              </div>
            )}
            {aendern && (
              <>
                <div>
                  <Label htmlFor="fints-label">Bezeichnung</Label>
                  <Input
                    id="fints-label"
                    value={felder.label}
                    onChange={(e) => setFelder({ ...felder, label: e.target.value })}
                    placeholder="Kreissparkasse Steinfurt"
                    data-testid="fints-label"
                  />
                </div>
                <div>
                  <Label htmlFor="fints-method">TAN-Verfahren</Label>
                  <select
                    id="fints-method"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={felder.auth_method}
                    onChange={(e) => setFelder({ ...felder, auth_method: e.target.value })}
                    data-testid="fints-method"
                  >
                    <option value="push_tan">Push-TAN</option>
                    <option value="tan">TAN eingeben</option>
                    <option value="keyfile">Schlüsseldatei</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="fints-blz">Bankleitzahl</Label>
                  <Input
                    id="fints-blz"
                    value={felder.bank_code}
                    onChange={(e) => setFelder({ ...felder, bank_code: e.target.value })}
                    inputMode="numeric"
                    autoComplete="off"
                    data-testid="fints-blz"
                  />
                </div>
                <div>
                  <Label htmlFor="fints-url">FinTS-Adresse</Label>
                  <Input
                    id="fints-url"
                    value={felder.endpoint_url}
                    onChange={(e) => setFelder({ ...felder, endpoint_url: e.target.value })}
                    placeholder="https://"
                    autoComplete="off"
                    data-testid="fints-url"
                  />
                </div>
                <div>
                  <Label htmlFor="fints-user">Benutzerkennung</Label>
                  <Input
                    id="fints-user"
                    value={felder.bank_user}
                    onChange={(e) => setFelder({ ...felder, bank_user: e.target.value })}
                    autoComplete="off"
                    data-testid="fints-user"
                  />
                </div>
                <div>
                  <Label htmlFor="fints-customer">Kunden-ID, falls die Bank sie getrennt führt</Label>
                  <Input
                    id="fints-customer"
                    value={felder.customer_id}
                    onChange={(e) => setFelder({ ...felder, customer_id: e.target.value })}
                    autoComplete="off"
                    data-testid="fints-customer"
                  />
                </div>
                <div>
                  <Label htmlFor="fints-account-iban">Zugehöriges Konto (IBAN)</Label>
                  <Input
                    id="fints-account-iban"
                    value={felder.account_iban}
                    onChange={(e) => setFelder({ ...felder, account_iban: e.target.value })}
                    autoComplete="off"
                    data-testid="fints-account-iban"
                  />
                </div>
                <div>
                  <Label htmlFor="fints-account-bic">BIC des Kontos (optional)</Label>
                  <Input
                    id="fints-account-bic"
                    value={felder.account_bic}
                    onChange={(e) => setFelder({ ...felder, account_bic: e.target.value })}
                    autoComplete="off"
                    data-testid="fints-account-bic"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Die Produktkennung muss bei der Deutschen Kreditwirtschaft registriert sein.
                  Ohne Registrierung weisen viele Banken die Verbindung ab.
                </p>
              </>
            )}
            {aktuelle && !aendern && (
              <p className="text-sm text-slate-600">
                {aktuelle.auth_method === 'push_tan' ? 'Push-TAN · ' : ''}
                {aktuelle.auth_method === 'keyfile' ? 'Schlüsseldatei · ' : ''}
                {aktuelle.bank_code} · {aktuelle.bank_user}
                {' '}
                <button type="button" className="underline" onClick={() => setAendern(true)} data-testid="fints-edit-connection">
                  ändern
                </button>
              </p>
            )}
            <div>
              <Label htmlFor="fints-pin">
                {(aktuelle?.auth_method === 'keyfile' && !aendern) || felder.auth_method === 'keyfile'
                  ? 'Passwort der Schlüsseldatei'
                  : 'PIN'}
              </Label>
              <Input
                id="fints-pin"
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                autoComplete="off"
                data-testid="fints-pin"
              />
              <p className="text-xs text-slate-500 mt-1">
                {(aktuelle?.auth_method === 'keyfile' && !aendern) || felder.auth_method === 'keyfile'
                  ? 'Das Passwort öffnet die Schlüsseldatei und wird nicht gespeichert.'
                  : 'Die PIN bleibt nicht in der Bankverbindung gespeichert.'}
              </p>
            </div>
          </div>
        )}

        {schritt === 'tan_wahl' && (
          <div className="space-y-2">
            <p className="text-sm text-slate-700">Welches TAN-Verfahren soll die Bank verwenden?</p>
            {verfahren.map((v) => (
              <Button
                key={v.id}
                type="button"
                variant="outline"
                className="w-full justify-start"
                disabled={busy}
                data-testid={`fints-mechanism-${v.id}`}
                onClick={() => senden({ tan_security_function: v.id })}
              >
                {v.name}
              </Button>
            ))}
          </div>
        )}

        {schritt === 'tan' && (
          <div className="space-y-3">
            <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="fints-challenge">{hinweis}</p>
            {entkoppelt ? (
              <p className="text-sm text-slate-600">Bitte den Auftrag in der Banking-App freigeben.</p>
            ) : (
              <div>
                <Label htmlFor="fints-tan">TAN</Label>
                <Input
                  id="fints-tan"
                  value={tan}
                  onChange={(e) => setTan(e.target.value)}
                  autoComplete="off"
                  data-testid="fints-tan"
                />
              </div>
            )}
          </div>
        )}

        {schritt === 'vop' && (
          <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="fints-vop">{hinweis}</p>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => schliessen(false)} disabled={busy}>Abbrechen</Button>
          {schritt === 'pin' && (
            <Button onClick={() => senden()} disabled={busy} data-testid="fints-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Landmark className="w-4 h-4 mr-1" />}
              An die Bank senden
            </Button>
          )}
          {schritt === 'tan' && !entkoppelt && (
            <Button onClick={() => fortsetzen(tan.trim())} disabled={busy} data-testid="fints-tan-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              TAN bestätigen
            </Button>
          )}
          {schritt === 'tan' && entkoppelt && (
            <Button onClick={() => fortsetzen('')} disabled={busy} data-testid="fints-app-bestaetigt">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              In der App bestätigt
            </Button>
          )}
          {schritt === 'vop' && (
            <Button onClick={() => fortsetzen('')} disabled={busy} data-testid="fints-vop-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Trotzdem einreichen
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
